<template>

  <FormKit
      v-if="basicDataLoaded"
      type="form"
      v-model="formData"
      @submit="handleSubmit"
      :actions="false">
    <div class="container-fluid">
      <div class="row">
        <!-- Required fields -->
        <div class="col-md-6">
          <myFormKit
              type="text"
              name="name"
              label="Název"
              validation="required"
          />
        </div>

        <div class="col-md-6">
          <myFormKit
              type="text"
              name="internal_note"
              label="Interní poznámka"
          />
        </div>

        <div class="col-md-4">
          <myFormKit
              type="number"
              name="price"
              label="Cena"
              validation="required|number"
              step="0.01"

          />
        </div>

        <!-- Foreign key selects -->
        <div class="col-md-4">
          <myFormKit
              type="select"
              name="price_list_id"
              label="Ceník"
              validation="required"
              :options="priceLists"
              input-class="form-select"
          />
        </div>

        <div class="col-md-4">
          <myFormKit
              type="select"
              name="material_id"
              label="Materiál"
              validation="required"
              input-class="form-select"
              :options="materials"
          />
        </div>

        <div class="col-md-6">
          <myFormKit
              type="select"
              name="units_id"
              label="Jednotky"
              validation="required"
              :options="units"
          />
        </div>

        <div class="col-md-6">
          <myFormKit
              type="select"
              name="thickness_id"
              label="Tloušťka"
              validation="required"
              :options="thicknesses"
          />
        </div>

        <!-- Optional fields -->
        <div class="col-md-6">
          <myFormKit
              type="text"
              name="ext_id"
              label="Externí ID"
          />
        </div>

        <div class="col-md-6">
          <myFormKit
              type="number"
              name="unit_count"
              label="Plocha desky [m²]"
              step="0.01"
              :disabled="true"
          />
        </div>

        <div class="col-md-6">
          <myFormKit
              type="number"
              name="weight"
              label="Váha [kg/m²]"
              step="0.01"
              validation="required|number"
          />
        </div>

        <div class="col-md-6">
          <myFormKit
              type="select"
              name="surface"
              label="Úprava povrchu"
              :options="surfaces"
          />
        </div>

        <!-- Boolean fields -->
        <div class="col-md-6">
          <div class="mt-4">
            <FormKit
                type="checkbox"
                name="is_directional"
                label="Směrové"
                input-class="form-check-input"
                wrapper-class="form-switch"
                label-class="mx-3"
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="mt-4">
            <FormKit
                type="checkbox"
                name="order_only"
                label="Pouze na objednávku"
                input-class="form-check-input"
                wrapper-class="form-switch"
                label-class="mx-3"
            />
          </div>
        </div>

        <!-- Required numeric fields -->
        <div class="col-md-6">
          <myFormKit
              type="number"
              name="cutoff"
              label="Prořez koeficient"
              validation="required|number"
              :validation-messages="{
            required: 'Odřezek je povinný',
            number: 'Zadejte platné číslo',
          }"
              step="0.01"
              :disabled="true"
          />
        </div>

        <!-- Optional numeric fields -->
        <div class="col-md-6">
          <myFormKit
              type="number"
              name="rate_calulated"
              label="Marže [%]"
              :value="calculatedRate"
              :disabled="true"
          />
        </div>

        <div class="col-md-6">
          <myFormKit
              type="number"
              name="rate_absolute"
              label="Absolutní prodejní cena"
              step="0.01"
              :disabled="true"
          />
        </div>

        <div class="row">
          <div class="col-md-6">
            <myFormKit
                type="number"
                name="dimension_x"
                label="Rozměr X [mm]"
                validation="required|number"
            />
          </div>

          <div class="col-md-6">
            <myFormKit
                type="number"
                name="dimension_y"
                label="Rozměr Y [mm]"
                validation="required|number"
            />
          </div>
        </div>

        <!-- Hidden fields -->
        <FormKit
            type="hidden"
            name="deleted"
            :value="false"
        />
      </div>
    </div>
  </FormKit>
</template>


<script setup>
import {ref, onMounted, watch, defineModel, computed} from 'vue'
import myFormKit from "@/components/formkit/MyFormKit.vue"
import {useStore} from "vuex";

const formData = defineModel()
const priceLists = ref([])
const materials = ref([])
const units = ref([])
const thicknesses = ref([])
const surfaces = ref([
  {label: 'lesklý', value: 'lesklý'},
  {label: 'kartácované', value: 'kartácované'},
  {label: 'leather', value: 'leather'},
  {label: 'opalovaný', value: 'opalovaný'},
  {label: 'touch finish', value: 'touch finish'},
  {label: 'satina', value: 'satina'},
  {label: 'tryskaný', value: 'tryskaný'},
  {label: 'antik', value: 'antik'},
  {label: 'waterjet', value: 'waterjet'},
  {label: 'broušený', value: 'broušený'},
  {label: 'terra', value: 'terra'},
  {label: 'matt', value: 'matt'},
])

const store = useStore();
const basicDataLoaded = ref(false)

// Handler pro odeslání formuláře
const handleSubmit = (data) => {
  // Přidání timestamps
  data.created_at = new Date().toISOString()
  data.updated_at = new Date().toISOString()
}

const calculateUnitCount = (dimensionX, dimensionY) => {
  return (dimensionX * dimensionY) / 1000000;
}

const calculatedRate = computed(() => {
  return formData.value.rate * 100;
})

watch(() => formData.value.is_directional, (newVal) => {
  if (newVal) {
    formData.value.cutoff = 1.4
  } else {
    formData.value.cutoff = 1.1
  }
})

watch(() => formData.value.dimension_x, (newVal) => {
  formData.value.unit_count = calculateUnitCount(newVal, formData.value.dimension_y)
})

watch(() => formData.value.dimension_y, (newVal) => {
  formData.value.unit_count = calculateUnitCount(formData.value.dimension_x, newVal)
})

function makeSelectObject(label, value) {
  return {label: label, value: value}
}


onMounted(async () => {
  for (const [key, data] of Object.entries(store.getters['price_list/getAllPriceList'])) {
      priceLists.value.push(makeSelectObject(data.name, key));
  }

  for (const [key, data] of Object.entries(store.getters['price_list/getAllMaterials'])) {
    materials.value.push(makeSelectObject(data.name, key));
  }

  for (const [key, data] of Object.entries(store.getters['price_list/getAllThicknesses'])) {
    thicknesses.value.push(makeSelectObject(data.name, key));
  }

  for (const [key, data] of Object.entries(store.getters['price_list/getAllUnits'])) {
    units.value.push(makeSelectObject(data.name, key));
  }

  basicDataLoaded.value = true
})
</script>